import React, { useContext, useState, useEffect } from "react";
import { TextField, Button, Box } from "@mui/material";
import { useForm } from "react-hook-form";
import { CommonContext } from "../../contexts/CommonContext";
import { AuthContext } from "../../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import {
  createNewUser,
  generateSignupOtp,
  getUserData,
  logAction,
  validateUserOtp,
} from "../../services/api";
import { getFirebaseError } from "../../services/error-codes";
import HomeLogo from "../../assets/home-logo.png";
import { Capacitor } from "@capacitor/core";

/**
 * Pilot-only login/signup. Duplicated from Authentication.jsx on purpose so
 * navigation and future pilot-specific behavior stay isolated from the main app /auth flow.
 */
const PILOT_HOME = "/pilot/home";

const styles = {
  homeLogo: {
    width: "80vw",
    maxWidth: "350px",
  },
  mainCont: {
    maxWidth: "500px",
    marginTop: "15vh",
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
  },
  mainContDesk: {
    marginTop: "15vh",
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    padding: "1vw 30vw 5vw 30vw",
  },
};

function PilotAuthentication() {
  const [showSignIn, setShowSignIn] = useState(false);
  const [deviceToken, setDeviceToken] = useState(null);
  const [showOtp, setShowOtp] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [impersoning, setImpersoning] = useState(false);

  const { showLoader, hideLoader, showAlert, showSnackbar, isDesktop } =
    useContext(CommonContext);
  const { userLoggedIn, setUserProfileData, isUserLoggedIn } =
    useContext(AuthContext);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const {
    register: registerOtp,
    handleSubmit: submitOtp,
    formState: { errors: errorsOtp },
  } = useForm();
  const {
    register: registerUser,
    handleSubmit: signupUser,
    formState: { errors: errorSignup },
  } = useForm();

  const navigate = useNavigate();
  const location = useLocation();

  const goToPilotAfterAuth = () => {
    const dest = location?.state?.redirectTo || PILOT_HOME;
    navigate(dest, { replace: true });
  };

  useEffect(() => {
    logAction("PAGE_VIEW", "pilot-authentication");
    async function checkLogin() {
      const resp = await isUserLoggedIn();
      if (resp) {
        goToPilotAfterAuth();
      }
    }
    checkLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const switchLogin = () => {
    setShowSignIn(!showSignIn);
    reset();
  };

  const loginUser = (data) => {
    if (data.mobileNo == process.env.REACT_APP_CUST_CARE_NO) {
      setImpersoning(true);
      showAlert(
        <>
          <b>You are now logging in as customer</b>
        </>
      );
      reset();
      return;
    }

    showLoader();
    logAction("login");
    getUserData(data.mobileNo, false)
      .then((response) => {
        if (response) {
          setUserProfile(response);
          sendOtp({
            channel: Capacitor.getPlatform(),
            mobileNo: data.mobileNo,
            firstName: response.f_name,
            type: "LOGIN",
          });
        } else {
          showAlert(<>User account is not registered. Please sign up.</>);
          hideLoader();
        }
      })
      .catch((error) => {
        hideLoader();
        showAlert(getFirebaseError(error));
      });
  };

  const signUpNewUser = (data) => {
    data.channel = Capacitor.getPlatform();
    logAction("signup");
    showLoader();
    getUserData(data.mobileNo, false)
      .then((response) => {
        if (response) {
          showAlert(<>Mobile number already registered. Please login.</>);
        } else {
          setUserProfile(data);
          data.type = "SIGN_UP";
          sendOtp(data);
        }
        hideLoader();
      })
      .catch((error) => {
        hideLoader();
        showAlert(getFirebaseError(error));
      });
  };

  const sendOtp = (data) => {
    data.impersoning = impersoning;
    generateSignupOtp(data)
      .then(() => {
        setShowOtp(true);
        hideLoader();
      })
      .catch((error) => {
        hideLoader();
        showAlert(error);
      });
  };

  const verifyOtp = async (data) => {
    showLoader();
    const reqData = {
      mobileNo: userProfile.mobileNo,
      otp: data.otp,
    };
    validateUserOtp(reqData)
      .then((response) => {
        if (response.err) {
          showAlert(response.err);
          hideLoader();
          return;
        }

        const userData = {
          userId: userProfile.mobileNo,
          deviceToken: deviceToken,
          timeStamp: Date.now(),
          ...userProfile,
        };

        if (!showSignIn) {
          createNewUser(userData)
            .then((createResp) => {
              setUserProfileData(userData);
              userLoggedIn(userProfile.mobileNo, createResp.customerId);
              hideLoader();
              showSnackbar("Signup successful");
              goToPilotAfterAuth();
            })
            .catch((error) => {
              hideLoader();
              showAlert(getFirebaseError(error.code));
            });
        } else {
          setUserProfileData(userProfile);
          userLoggedIn(userProfile.mobileNo, userProfile.customerId);
          hideLoader();
          showSnackbar("Login successful");
          goToPilotAfterAuth();
        }
      })
      .catch((error) => {
        hideLoader();
        showAlert(error);
      });
  };

  return (
    <Box style={isDesktop ? styles.mainContDesk : styles.mainCont}>
      <Box>
        <Box sx={{ textAlign: "center", marginTop: "2vh" }}>
          <img src={HomeLogo} style={styles.homeLogo} alt="" />
        </Box>
        {showOtp ? (
          <Box
            sx={{
              textAlign: "center",
              padding: "4vw",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box sx={{ width: "100%" }}>
              <form onSubmit={submitOtp(verifyOtp)}>
                <h5>Enter OTP sent to your mobile number</h5>
                <Box mb={3}>
                  <TextField
                    placeholder="Enter OTP"
                    label="OTP Number"
                    variant="outlined"
                    fullWidth
                    type="number"
                    autoComplete="off"
                    name="otp"
                    {...registerOtp("otp", {
                      required: "Required field",
                    })}
                    error={Boolean(errorsOtp?.otp)}
                    helperText={errorsOtp?.otp?.message}
                  />
                </Box>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  id="pilot-sign-in-button"
                >
                  Verify OTP
                </Button>
              </form>
            </Box>
          </Box>
        ) : null}
      </Box>
      <Box>
        {showOtp ? null : (
          <Box>
            {showSignIn ? (
              <Box p={2}>
                <h2>Login</h2>
                <Box>
                  <form onSubmit={handleSubmit(loginUser)} key={1}>
                    <Box mb={3}>
                      <TextField
                        placeholder="Enter your mobile number"
                        label="Mobile Number"
                        variant="outlined"
                        fullWidth
                        autoComplete="off"
                        type="number"
                        name="mobileNo"
                        {...register("mobileNo", {
                          required: "Required field",
                          pattern: {
                            value: /^[7896]\d{9}$/,
                            message: "Invalid mobile number",
                          },
                        })}
                        error={Boolean(errors?.mobileNo)}
                        helperText={errors?.mobileNo?.message}
                      />
                    </Box>

                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth
                    >
                      Log In
                    </Button>
                  </form>
                  <Box
                    onClick={switchLogin}
                    sx={{ textAlign: "center", marginTop: "35px" }}
                  >
                    <Button variant="outlined">Create Account</Button>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box p={2}>
                <h2>Sign Up</h2>
                <form onSubmit={signupUser(signUpNewUser)} key={2}>
                  <Box mb={3}>
                    <TextField
                      placeholder="Enter your full name"
                      label="Full Name"
                      variant="outlined"
                      fullWidth
                      autoComplete="off"
                      name="firstName"
                      {...registerUser("firstName", {
                        required: "Required field",
                      })}
                      error={Boolean(errorSignup?.firstName)}
                      helperText={errorSignup?.firstName?.message}
                    />
                  </Box>

                  <Box mb={3}>
                    <TextField
                      placeholder="Enter your mobile number"
                      label="Mobile Number"
                      variant="outlined"
                      fullWidth
                      autoComplete="off"
                      type="number"
                      name="mobileNo"
                      {...registerUser("mobileNo", {
                        required: "Required field",
                        pattern: {
                          value: /^[7896]\d{9}$/,
                          message: "Invalid mobile number",
                        },
                      })}
                      error={Boolean(errorSignup?.mobileNo)}
                      helperText={errorSignup?.mobileNo?.message}
                    />
                  </Box>

                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                  >
                    Sign Up
                  </Button>
                </form>

                <Box
                  onClick={switchLogin}
                  sx={{ textAlign: "center", marginTop: "35px" }}
                >
                  <Button variant="outlined">Login</Button>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default PilotAuthentication;
